import api from './api'

export interface ConversationListItem {
  id: string
  waContact: string
  contactName?: string
  status: 'open' | 'closed' | 'QUEUED'
  mode: 'bot' | 'human'
  lastMessage?: string
  lastMessageAt?: Date
  unreadCount: number
  waInstanceId?: string
  channelInstanceId?: string
  connectionStatus?: 'CREATED' | 'CONNECTED' | 'DISCONNECTED' | 'PENDING' | 'ERROR'
  assignedUserId?: string | null
  assignedTeamId?: string | null
  assignedUserName?: string | null
  waInstance?: {
    id: string
    instanceName: string
    phoneNumber?: string
  }
  channelType?: string
  closeReason?: string | null
  closeNotes?: string | null
  closedAt?: string | null
  // Tags for conversations
  tags?: Tag[]
  commercialStatus?: {
    status: 'pending_billing' | 'pickup' | 'delivery'
    totalAmount?: number
    itemCount?: number
    fulfillmentMethod?: string
    productName?: string
  } | null
  isEscalatedByBot?: boolean
  escalationReason?: string | null
  isHitlPending?: boolean
  /** Hay una duda del asistente esperando respuesta del equipo en este chat. */
  hasPendingStaffConsultation?: boolean
  pendingStaffConsultationQuestion?: string | null
}

// Tag interface for conversations
export interface Tag {
  id: string
  name: string
  color: string
}

export interface ConversationHandoffSummary {
  reason?: string
  reasonDetail?: string
  customerName?: string
  customerPhone?: string
  fulfillmentMethod?: 'pickup' | 'delivery' | 'dine_in'
  fulfillmentAddress?: string
  items: Array<{
    name: string
    quantity: number
    price?: number
  }>
  totalAmount?: number
  currency?: string
  billingMode?: string
  actionRequired?: string
  timestamp?: string
}

export interface ConversationSalesContext {
  runtime: 'langgraph' | 'legacy' | 'none'
  stage?: string
  dominantIntent?: string
  lastMentionedProductName?: string
  lastMentionedProductId?: string
  frustrationScore?: number
  checkoutReadiness?: number
  activeOrderDraft?: {
    items: Array<{
      productId?: string
      title?: string
      quantity: number
      unitPriceCents?: number
    }>
    fulfillmentMethod?: string
    fulfillmentAddress?: string
    fulfillmentReference?: string
    subtotalCents?: number
    totalCents?: number
    currency?: string
    status?: string
  } | null
  product?: {
    id?: string
    title?: string
    price?: number
    sku?: string
  } | null
  handoff?: ConversationHandoffSummary | null
}

export interface PreviousConversationSummary {
  id: string
  createdAt: string
  closedAt: string | null
  closeReason: string | null
  closeNotes: string | null
  messages: ConversationMessage[]
}

export interface ConversationDetail {
  id: string
  waContact: string
  contactName?: string
  status: 'open' | 'closed' | 'QUEUED'
  mode: 'bot' | 'human'
  assignedUserId?: string
  assignedTeamId?: string
  assignedUserName?: string | null
  createdAt: Date
  updatedAt: Date
  autoCloseAt?: Date
  waInstanceId?: string
  channelInstanceId?: string
  waInstance?: {
    id: string
    instanceName: string
    phoneNumber?: string
    profileName?: string
  }
  avatarUrl?: string
  channelType?: string
  messages: ConversationMessage[]
  salesContext?: ConversationSalesContext
  closeReason?: string | null
  closeNotes?: string | null
  closedAt?: string | null
  closedByUserId?: string | null
  /** Id de la sesión activa más reciente del mismo contacto, si esta está cerrada. */
  activeConversationId?: string
  /** Sesiones cerradas previas del mismo contacto (asc), con sus mensajes. */
  previousConversations?: PreviousConversationSummary[]
}

export interface ConversationMessage {
  id: string
  messageId: string
  content: string
  direction: 'in' | 'out'
  waFrom: string
  waTo: string
  timestamp: Date
  status?: 'sent' | 'delivered' | 'read' | 'scheduled' | 'cancelled'
  type: 'text' | 'image' | 'audio' | 'video' | 'document' | 'sticker'
  hasMedia?: boolean
  mediaUrl?: string
  mediaMimetype?: string
  mediaFilename?: string
  ack?: number
  isRevoked?: boolean
  replyToMessageId?: string
  source?: 'ai' | 'faq' | 'rule' | 'product' | 'human' | string // Identifica origen del mensaje
  scheduledAt?: Date // Fecha programada para mensajes programados
  reactions?: {
    id: string
    emoji: string
    from: string
    fromLabel?: string
  }[]
}

export interface ConversationFilters {
  status?: 'open' | 'closed' | 'all'
  mode?: 'bot' | 'human' | 'all'
  hasUnread?: boolean
  instanceId?: string
  channelType?: string
  search?: string
  tags?: string[]
  closeReason?: string
  page?: number
  limit?: number
}

export interface ConversationsResponse {
  conversations: ConversationListItem[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  unreadByChannel?: Record<string, number>
  totalByChannel?: Record<string, number>
}

export interface ConversationViewCounts {
  mine: number
  team: number
  all: number
  open?: number
  closed?: number
}

export interface SendMessageRequest {
  content?: string
  type?: 'text' | 'image' | 'audio' | 'video' | 'document'
  media?: {
    mimetype: string
    filename: string
    data?: string
    url?: string
  }
  scheduledAt?: string // ISO 8601 datetime string for scheduled messages
}

export interface InitiateConversationRequest {
  waContact: string
  content: string
  channelInstanceId?: string
  channelType?: string
}

export interface EscalateConversationRequest {
  mode: 'bot' | 'human'
  reason?: string
  hitlResolution?: {
    action?: 'approve' | 'edit' | 'deny'
    notes?: string
    summary?: string
    confirmedProductId?: string
    agreedPriceCents?: number
    targetStage?: string
  }
}

export interface UpdateConversationStatusRequest {
  status: 'open' | 'closed'
  closeReason?: string
  closeNotes?: string
}

export interface HitlStatusResponse {
  pending: boolean
  request: Record<string, unknown> | null
  traceId: string | null
  traceCreatedAt: string | null
  summary?: string | null
  resolution?: Record<string, unknown> | null
  pausedAt?: string | null
  updatedAt?: string | null
}

export type StaffConsultationTopic = 'delivery_fee' | 'general' | 'catalog'

/** Duda que el asistente no pudo resolver y que una persona del equipo debe contestar. */
export interface StaffConsultation {
  id: string
  conversationId: string
  topic: StaffConsultationTopic
  question: string
  customerMessage: string | null
  context: Record<string, unknown> | null
  status: 'pending' | 'answered' | 'taken_over' | 'expired'
  answer: string | null
  answeredAt: string | null
  createdAt: string
}

export interface StaffConsultationAnswerRequest {
  message: string
  deliveryFeeUsd?: number | null
  zoneName?: string | null
  saveZone?: boolean
}

export type StaffConsultationAnswerResponse = StaffConsultation & { zoneSaved: boolean }

export interface HitlResumeRequest {
  action: 'approve' | 'edit' | 'deny'
  message?: string
  reason?: string
  intent?: string
  stage?: string
}

export interface HitlResumeResponse {
  success: boolean
  pendingResponse: string | null
  durationMs: number
  finalRoute: string
  hitlPaused: boolean
  hitlRequest: unknown
  error: string | null
  status: HitlStatusResponse
  messageSent: boolean
  messageId: string | null
  deliveryError: string | null
}

export const conversationsApi = {
  // Lista conversaciones filtradas por tenant
  async getConversations(filters: ConversationFilters = {}): Promise<ConversationsResponse> {
    const params = new URLSearchParams()

    if (filters.status) params.append('status', filters.status)
    if (filters.mode) params.append('mode', filters.mode)
    if (filters.hasUnread !== undefined) params.append('hasUnread', filters.hasUnread.toString())
    if (filters.instanceId) params.append('instanceId', filters.instanceId)
    if (filters.channelType && filters.channelType !== 'all') params.append('channelType', filters.channelType)
    if (filters.search) params.append('search', filters.search)
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.tags && filters.tags.length > 0) {
      filters.tags.forEach(tag => params.append('tags', tag))
    }

    const response = await api.get(`/conversations?${params.toString()}`)

    return {
      conversations: response.data.data.map((conv: any) => ({
        ...conv,
        lastMessageAt: conv.lastMessageAt ? new Date(conv.lastMessageAt) : undefined,
      })),
      pagination: response.data.pagination,
      unreadByChannel: response.data.meta?.unreadByChannel || {},
    }
  },

  // Contadores reales por pestaña (Asignados / Equipo / Todos / Abiertas / Cerradas)
  async getViewCounts(filters: Pick<ConversationFilters, 'channelType' | 'mode' | 'status'> = {}): Promise<ConversationViewCounts> {
    const params = new URLSearchParams()
    if (filters.mode && filters.mode !== 'all') params.append('mode', filters.mode)
    if (filters.channelType && filters.channelType !== 'all') params.append('channelType', filters.channelType)
    if (filters.status && filters.status !== 'all') params.append('status', filters.status)
    const qs = params.toString()
    const response = await api.get(`/conversations/counts${qs ? `?${qs}` : ''}`)
    return response.data.data as ConversationViewCounts
  },

  // Obtener conversación específica con mensajes
  async getConversationById(id: string): Promise<ConversationDetail> {
    const response = await api.get(`/conversations/${id}`)
    const conv = response.data.data

    return {
      ...conv,
      createdAt: new Date(conv.createdAt),
      updatedAt: new Date(conv.updatedAt),
      autoCloseAt: conv.autoCloseAt ? new Date(conv.autoCloseAt) : undefined,
      messages: conv.messages.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
        scheduledAt: msg.scheduledAt ? new Date(msg.scheduledAt) : undefined,
      })),
    }
  },

  // Enviar mensaje desde dashboard como agente
  async sendMessage(conversationId: string, messageData: SendMessageRequest) {
    const response = await api.post(`/conversations/${conversationId}/messages`, messageData)
    return response.data.data
  },

  async initiateConversation(payload: InitiateConversationRequest) {
    const response = await api.post('/conversations/initiate', payload)
    return response.data.data
  },

  // Escalamiento manual de bot a agente humano
  async escalateConversation(conversationId: string, escalationData: EscalateConversationRequest) {
    const response = await api.put(`/conversations/${conversationId}/escalate`, escalationData)
    return response.data.data
  },

  // Cambiar status de conversación: "open" ↔ "closed"
  async updateConversationStatus(conversationId: string, statusData: UpdateConversationStatusRequest) {
    const response = await api.put(`/conversations/${conversationId}/status`, statusData)
    return response.data.data
  },

  async getHitlStatus(conversationId: string): Promise<HitlStatusResponse> {
    const response = await api.get(`/conversations/${conversationId}/hitl/status`)
    return response.data.data
  },

  async getStaffConsultation(conversationId: string): Promise<StaffConsultation | null> {
    const response = await api.get(`/conversations/${conversationId}/staff-consultation`)
    return response.data.data ?? null
  },

  async answerStaffConsultation(
    conversationId: string,
    consultationId: string,
    payload: StaffConsultationAnswerRequest
  ): Promise<StaffConsultationAnswerResponse> {
    const response = await api.post(`/conversations/${conversationId}/staff-consultation/${consultationId}/answer`, payload)
    return response.data.data
  },

  async takeOverStaffConsultation(conversationId: string, consultationId: string): Promise<StaffConsultation> {
    const response = await api.post(`/conversations/${conversationId}/staff-consultation/${consultationId}/takeover`)
    return response.data.data
  },

  async resumeHitl(conversationId: string, payload: HitlResumeRequest): Promise<HitlResumeResponse> {
    const response = await api.post(`/conversations/${conversationId}/hitl/resume`, payload)
    return response.data.data
  },

  // Vaciar todos los mensajes de una conversación (dejar chat en blanco)
  async clearConversationMessages(conversationId: string): Promise<{ deletedCount: number }> {
    const response = await api.delete(`/conversations/${conversationId}/messages`)
    return response.data.data
  },

  // Eliminar mensaje
  async deleteMessage(messageId: string): Promise<void> {
    await api.delete(`/messages/${messageId}`)
  },

  // Agregar o remover reacción a un mensaje
  async addReaction(messageId: string, reaction: string): Promise<void> {
    await api.put(`/messages/${encodeURIComponent(messageId)}/reaction`, { reaction })
  },

  // Marcar mensaje como visto
  async markAsSeen(messageId: string): Promise<void> {
    await api.post(`/messages/${messageId}/seen`)
  },

  // Cancelar un mensaje programado
  async cancelScheduledMessage(messageId: string): Promise<void> {
    await api.delete(`/messages/${messageId}/scheduled`)
  },

  // Marcar conversación completa como leída por el agente actual
  async markConversationAsRead(conversationId: string): Promise<void> {
    await api.post(`/conversations/${conversationId}/read`)
  },

  // Claim (tomar) una conversación pendiente
  async claimConversation(conversationId: string): Promise<ConversationListItem> {
    const response = await api.post(`/conversations/${conversationId}/claim`)
    return {
      ...response.data.conversation,
      lastMessageAt: response.data.conversation.lastMessageAt
        ? new Date(response.data.conversation.lastMessageAt)
        : undefined,
    }
  },

  // Transferir conversación a un equipo
  async transferConversation(conversationId: string, teamId: string): Promise<ConversationListItem> {
    const response = await api.post(`/conversations/${conversationId}/transfer`, { teamId })
    return {
      ...response.data,
      lastMessageAt: response.data.lastMessageAt
        ? new Date(response.data.lastMessageAt)
        : undefined,
    }
  },

  // ==================== Internal Notes ====================

  // Obtener notas de una conversación
  async getNotes(conversationId: string): Promise<InternalNote[]> {
    const response = await api.get(`/conversations/${conversationId}/notes`)
    return response.data.data.map((note: any) => ({
      ...note,
      createdAt: new Date(note.createdAt),
      updatedAt: note.updatedAt ? new Date(note.updatedAt) : undefined,
    }))
  },

  // Crear una nota
  async createNote(conversationId: string, content: string): Promise<InternalNote> {
    const response = await api.post(`/conversations/${conversationId}/notes`, { content })
    return {
      ...response.data.data,
      createdAt: new Date(response.data.data.createdAt),
      updatedAt: response.data.data.updatedAt ? new Date(response.data.data.updatedAt) : undefined,
    }
  },

  // Actualizar una nota
  async updateNote(conversationId: string, noteId: string, content: string): Promise<InternalNote> {
    const response = await api.put(`/conversations/${conversationId}/notes/${noteId}`, { content })
    return {
      ...response.data.data,
      createdAt: new Date(response.data.data.createdAt),
      updatedAt: response.data.data.updatedAt ? new Date(response.data.data.updatedAt) : undefined,
    }
  },

  // Eliminar una nota
  async deleteNote(conversationId: string, noteId: string): Promise<void> {
    await api.delete(`/conversations/${conversationId}/notes/${noteId}`)
  },
}

// Internal Note types
export interface InternalNote {
  id: string
  tenantId: string
  conversationId: string
  userId: string | null
  content: string
  source?: 'manual' | 'bot_summary'
  createdAt: Date
  updatedAt?: Date
  user?: {
    id: string
    name: string | null
    email: string
  } | null
}
